import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import api from "@/api/client";
import { Send, Sparkles, Bot, User as UserIcon, RotateCcw, History, Sprout } from "lucide-react";
import { toast } from "sonner";
import SeoHead from "@/components/seo/SeoHead";

const CropAdvisor = () => {
  const { user, ready } = useAuth();
  const { t } = useI18n();
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [drawer, setDrawer] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    api.get("/ai/advisor/sessions").then((r) => setSessions(r.data));
  }, [user]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  if (!ready) return <div className="container-fah py-20 text-center text-muted-foreground">Loading…</div>;
  if (!user) {
    return (
      <div className="container-fah py-20 text-center" data-testid="advisor-login-required">
        <Bot className="w-12 h-12 mx-auto text-primary" />
        <h2 className="font-display text-2xl font-bold mt-3">{t("advisor.title")}</h2>
        <p className="text-muted-foreground mt-2">Please login to chat with KrishiBot.</p>
        <Link to="/login" className="mt-5 inline-block bg-primary text-white font-semibold py-2.5 px-5 rounded-lg">Login</Link>
      </div>
    );
  }

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || sending) return;
    setSending(true);
    setInput("");
    const userMsg = { id: `u-${Date.now()}`, role: "user", content: text, created_at: new Date().toISOString() };
    setMessages((m) => [...m, userMsg]);
    try {
      const { data } = await api.post("/ai/advisor/chat", { session_id: sessionId, message: text });
      if (!sessionId) setSessionId(data.session_id);
      setMessages((m) => [...m, data.reply]);
      api.get("/ai/advisor/sessions").then((r) => setSessions(r.data));
    } catch (ex) {
      toast.error("AI advisor is unavailable. Please try again.");
      setMessages((m) => m.filter((x) => x.id !== userMsg.id));
    } finally { setSending(false); }
  };

  const loadSession = async (sid) => {
    const { data } = await api.get(`/ai/advisor/history/${sid}`);
    setMessages(data); setSessionId(sid); setDrawer(false);
  };

  const newChat = () => { setSessionId(null); setMessages([]); };

  const SUGGESTIONS = [t("advisor.suggest.1"), t("advisor.suggest.2"), t("advisor.suggest.3"), t("advisor.suggest.4")];

  return (
    <div className="container-fah py-6 sm:py-8 max-w-4xl" data-testid="page-crop-advisor">
      <SeoHead
        title="KrishiBot AI — Free Crop Advisor in Hindi & English"
        description="Get instant agricultural advice from KrishiBot AI. Ask about crops, pests, fertilizers, irrigation, sowing schedules and government schemes in Hindi or English. Free, 24/7."
        path="/crop-advisor"
      />
      <div className="flex items-end justify-between gap-4 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary to-primary-hover text-white flex items-center justify-center">
            <Sprout className="w-7 h-7" />
          </div>
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold flex items-center gap-2">{t("advisor.title")} <span className="text-[10px] font-bold uppercase tracking-wider bg-accent text-accent-foreground px-2 py-0.5 rounded-full">Beta</span></h1>
            <p className="text-sm text-muted-foreground">{t("advisor.sub")}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setDrawer(true)} className="hidden sm:flex items-center gap-1 text-sm px-3 py-2 border border-border rounded-lg hover:bg-muted" data-testid="btn-advisor-history"><History className="w-4 h-4" /> History</button>
          <button onClick={newChat} className="flex items-center gap-1 text-sm bg-primary text-white px-3 py-2 rounded-lg hover:bg-primary-hover" data-testid="btn-advisor-new"><RotateCcw className="w-4 h-4" /> New</button>
        </div>
      </div>

      <div className="card-fah flex flex-col h-[70vh]">
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4" data-testid="advisor-messages">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-8">
              <div className="w-16 h-16 rounded-2xl bg-accent flex items-center justify-center mb-4">
                <Sparkles className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-display font-bold text-lg max-w-md">{t("advisor.empty")}</h3>
              <div className="mt-6 grid sm:grid-cols-2 gap-2 w-full max-w-xl">
                {SUGGESTIONS.map((s, i) => (
                  <button key={i} onClick={() => send(s)} className="text-left text-sm px-4 py-3 border border-border rounded-xl hover:border-primary hover:bg-accent/40 transition" data-testid={`advisor-suggest-${i}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((m) => <Bubble key={m.id} msg={m} />)
          )}
          {sending && (
            <div className="flex items-start gap-3" data-testid="advisor-typing">
              <Avatar role="assistant" />
              <div className="bg-muted/60 rounded-2xl rounded-tl-sm px-4 py-3">
                <div className="flex gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary/70 animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-primary/70 animate-bounce" style={{ animationDelay: "120ms" }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-primary/70 animate-bounce" style={{ animationDelay: "240ms" }} />
                </div>
              </div>
            </div>
          )}
        </div>

        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-border p-3 flex items-center gap-2 bg-white" data-testid="advisor-form">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={t("advisor.placeholder")}
            className="flex-1 px-4 py-3 bg-muted/40 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            data-testid="advisor-input"
            disabled={sending}
          />
          <button type="submit" disabled={sending || !input.trim()} className="bg-primary hover:bg-primary-hover text-white font-semibold px-4 py-3 rounded-xl flex items-center gap-1 disabled:opacity-40" data-testid="advisor-send">
            <Send className="w-4 h-4" /> <span className="hidden sm:inline">{t("advisor.send")}</span>
          </button>
        </form>
      </div>

      <p className="text-[11px] text-muted-foreground mt-3 text-center">KrishiBot is an AI assistant and may occasionally provide inaccurate information. Always cross-check with local KVK officers for critical decisions.</p>

      {drawer && (
        <div className="fixed inset-0 z-50 bg-black/40" onClick={() => setDrawer(false)}>
          <div className="absolute right-0 top-0 bottom-0 w-80 bg-white p-5 overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="advisor-history-drawer">
            <h3 className="font-display font-bold mb-4">Chat History</h3>
            {sessions.length === 0 ? <p className="text-sm text-muted-foreground">No conversations yet.</p> : (
              <div className="space-y-2">
                {sessions.map((s) => (
                  <button key={s.session_id} onClick={() => loadSession(s.session_id)} className={`w-full text-left p-3 rounded-lg border ${sessionId === s.session_id ? "border-primary bg-accent/40" : "border-border hover:bg-muted"}`}>
                    <div className="text-xs text-muted-foreground">{new Date(s.last_at).toLocaleDateString("en-IN")} · {s.count} msgs</div>
                    <div className="text-sm font-medium line-clamp-2 mt-1">{s.last_message}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Avatar = ({ role }) => (
  role === "assistant"
    ? <div className="w-9 h-9 rounded-full bg-primary text-white flex items-center justify-center shrink-0"><Bot className="w-5 h-5" /></div>
    : <div className="w-9 h-9 rounded-full bg-secondary/15 text-secondary flex items-center justify-center shrink-0"><UserIcon className="w-5 h-5" /></div>
);

const Bubble = ({ msg }) => {
  const isUser = msg.role === "user";
  return (
    <div className={`flex items-start gap-3 ${isUser ? "flex-row-reverse" : ""}`} data-testid={`msg-${msg.role}`}>
      <Avatar role={msg.role} />
      <div className={`max-w-[85%] sm:max-w-[75%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${isUser ? "bg-primary text-white rounded-tr-sm" : "bg-muted/60 text-foreground rounded-tl-sm"}`}>
        <FormattedText text={msg.content} />
      </div>
    </div>
  );
};

const FormattedText = ({ text }) => {
  // Basic markdown-lite: headings, bold, bullets, line breaks
  const lines = text.split("\n");
  return (
    <div className="space-y-1">
      {lines.map((line, i) => {
        if (line.startsWith("# ")) return <div key={i} className="font-display font-bold text-base mt-2">{line.slice(2)}</div>;
        if (line.startsWith("## ")) return <div key={i} className="font-display font-semibold text-sm mt-2">{line.slice(3)}</div>;
        if (line.match(/^[-*•]\s/)) return <div key={i} className="ml-3 before:content-['•'] before:mr-2 before:text-primary">{renderInline(line.replace(/^[-*•]\s/, ""))}</div>;
        if (line.match(/^\d+\.\s/)) return <div key={i} className="ml-1">{renderInline(line)}</div>;
        if (!line.trim()) return <div key={i} className="h-1" />;
        return <div key={i}>{renderInline(line)}</div>;
      })}
    </div>
  );
};

const renderInline = (text) => {
  // Bold **x**
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) return <strong key={i}>{p.slice(2, -2)}</strong>;
    return <span key={i}>{p}</span>;
  });
};

export default CropAdvisor;
